/**
 * RejectDialog — modal for capturing a rejection reason when a reviewer requests changes
 * on a pending commit request. See specs/phase2/CR-D-artifact-editing-frontend-spec.md §3.8.
 *
 * On submit it calls the reject endpoint; the artifact then moves to 'rejected' server-side
 * (Amendment A-1) so the GET response surfaces `last_rejection` for the RejectionBanner.
 */

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { useRejectCommitRequest } from "@/hooks/useArtifacts";

const MIN_REASON = 10;
const MAX_REASON = 500;

export interface RejectDialogProps {
  open: boolean;
  artifactType: string;
  projectId: string;
  reqId: string;
  onRejected: () => void;
  onClose: () => void;
}

export function RejectDialog({
  open,
  artifactType,
  projectId,
  reqId,
  onRejected,
  onClose,
}: RejectDialogProps): JSX.Element {
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const reject = useRejectCommitRequest(projectId, artifactType);

  // Reset the form each time the dialog is (re)opened.
  useEffect(() => {
    if (open) {
      setReason("");
      setTouched(false);
    }
  }, [open]);

  const trimmedLength = reason.trim().length;
  const tooShort = trimmedLength < MIN_REASON;
  const showError = touched && tooShort;

  const handleSubmit = async (): Promise<void> => {
    if (tooShort) {
      setTouched(true);
      return;
    }
    try {
      await reject.mutateAsync({ reqId, rejection_reason: reason.trim() });
      toast.success("Feedback sent");
      onRejected();
      onClose();
    } catch {
      // The shared axios interceptor surfaces a toast for HTTP errors; keep the dialog open.
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Request Changes</DialogTitle>
          <DialogDescription>
            Explain what needs to change. Your feedback returns the artifact to
            the editor so it can be revised and resubmitted.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-1">
          <Label htmlFor="reject-reason">
            Reason <span className="text-error">*</span>
          </Label>
          <Textarea
            id="reject-reason"
            value={reason}
            maxLength={MAX_REASON}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
            placeholder="Describe the changes required (minimum 10 characters)…"
            className="min-h-[120px]"
            aria-invalid={showError}
            aria-describedby="reject-reason-help reject-reason-count"
            disabled={reject.isPending}
          />
          <div className="flex items-center justify-between text-xs">
            <span
              id="reject-reason-help"
              className={cn(
                "text-text-secondary",
                showError && "text-error",
              )}
            >
              {showError ? "Please provide at least 10 characters" : "\u00A0"}
            </span>
            <span id="reject-reason-count" className="text-text-secondary">
              {reason.length}/{MAX_REASON}
            </span>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={reject.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={tooShort || reject.isPending}
          >
            {reject.isPending && (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            )}
            {reject.isPending ? "Sending…" : "Send Feedback"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default RejectDialog;
